import Phaser from 'phaser';
import {
  FIELD_AMBIENCE_DEPTH,
  FIELD_AMBIENCE_MAX_STEP_MS,
  FIELD_BIRD_TEXTURE_KEY,
  FIELD_BIRDS,
  FIELD_CLOUDS,
  FIELD_CLOUDS_SHEET,
  FIELD_EAGLE_IMAGE,
  FIELD_EAGLE_TEXTURE_KEY,
  FIELD_EAGLES,
  getFieldAmbience,
} from '../configs/fieldAmbience';
import {
  buildBirdFlock,
  checkIfBirdFlockDone,
  resolveBirdFlockDelayMs,
  resolveBirdPose,
} from '../utils/effects/buildBirdFlock';
import {
  advanceCloudStormLevel,
  advanceClouds,
  advanceStormClouds,
  buildCloudLayout,
  buildFieldCoverage,
  resolveCloudDrift,
  resolveCloudFieldCoverage,
  resolveCloudFieldFactor,
  resolveCloudHeight,
  resolveCloudShadowPlace,
  resolveGroundEdgeFade,
  resolveStormCloudCount,
} from '../utils/effects/buildCloudField';
import {
  buildEagleFlight,
  checkIfEagleFlightDone,
  resolveEagleDelayMs,
  resolveEaglePose,
} from '../utils/effects/buildEagleFlight';
import {
  BIRD_TEXTURE_WINGSPAN,
  EAGLE_TEXTURE_WINGSPAN,
  ensureBirdTexture,
  ensureEagleTexture,
} from '../utils/effects/fieldAmbienceTextures';
import {
  SNOW_SQUALL_TAG,
  resolveSnowSquallStreamSpeed,
  resolveSnowSquallStrength,
} from '../utils/effects/biome/snowSquall';
import FieldBiomeEffects from './FieldBiomeEffects';
import FieldEffectSounds from './FieldEffectSounds';
import FieldAmbienceSounds from './FieldAmbienceSounds';
import {EAGLE_SOUND_IDS, getFlockSoundIds} from '../configs/fieldAmbienceSounds';
import {checkIfAnySpriteInView, estimateInViewMs} from '../utils/effects/flightView';

// Слабее этого спрайт не рисуется вовсе: у края пейзажа облака и птицы
// невидимы, и гонять их через видеокарту незачем
const MIN_VISIBLE_ALPHA = 0.004;

// Заряда вьюги нет: общий ответ, чтобы не заводить новый на каждом кадре
const NO_SQUALL = Object.freeze({strength: 0, streamSpeed: 0});

/** Спрайт — на место, с заданной плотностью; невидимый не рисуется вовсе */
const placeSprite = (sprite, x, y, alpha) => {
  sprite.setPosition(x, y);
  sprite.setAlpha(alpha);
  sprite.setVisible(alpha > MIN_VISIBLE_ALPHA);
};

/** Убрать спрайт; сбой на одном не должен оставить на сцене остальные */
const destroySprite = (sprite) => {
  if (!sprite) {
    return;
  }
  try {
    sprite.destroy();
  } catch (error) {
    console.warn('[FieldAmbience] Не удалось убрать спрайт антуража', error);
  }
};

/**
 * Ветер матча: по признаку партии, а где подложка закрепила сторону ветра
 * (на ее картинке нарисован дым, который тянется в одну сторону) — в эту сторону
 */
const resolveDrift = (seed, settings) => {
  const drift = resolveCloudDrift(seed);
  return settings.windDirection ? {...drift, direction: settings.windDirection} : drift;
};

/** Признак места на поле: по нему орел помнит свою гору */
const resolvePlaceKey = ({x, y}) => `${Math.round(x)}:${Math.round(y)}`;

/**
 * Полоса, в которой проходит курс стаи: игровое поле с запасом сверху и
 * снизу — стая летит над полем или рядом с ним, а не где-то на краю пейзажа.
 *
 * @returns {{top: number, bottom: number}|undefined} не задана — поля нет
 */
const resolveBirdLane = (cellCenters, hexSize) => {
  if (!cellCenters?.length) {
    return undefined;
  }
  let top = Infinity;
  let bottom = -Infinity;
  for (let index = 0; index < cellCenters.length; index += 1) {
    top = Math.min(top, cellCenters[index].y);
    bottom = Math.max(bottom, cellCenters[index].y);
  }
  const margin = hexSize * FIELD_BIRDS.laneFieldMarginFactor;
  return {top: top - margin, bottom: bottom + margin};
};

/**
 * АНТУРАЖ ПОЛЯ: облака с тенями, плывущие над пейзажем и полем, стаи птиц,
 * изредка пролетающие над ним, и орлы, кружащие над горами. На поле мы
 * смотрим с большой высоты, как на карту мира.
 *
 * Спрайты принадлежат сцене, а не ячейкам поля: перерисовка по стору
 * (GameScene.handleStoreUpdate) их не трогает. Двигает их каждый кадр
 * GameScene.update — по времени кадра, а не по настенным часам: в скрытой
 * вкладке антураж стоит и после возврата продолжает с того же места.
 *
 * ВЕТЕР НА ВЕСЬ МАТЧ ОДИН. Направление облаков считается из признака партии
 * (время ее начала): оно одно и то же при любой пересборке сцены и после
 * перезагрузки страницы. Пока партия не началась (редактирование поля,
 * обучение со слепка) признака нет — ветер берется по подложке.
 *
 * НАД ИГРОВЫМ ПОЛЕМ ОБЛАКА ВИДНЫ, но прозрачнее, чем над пейзажем: отряды и
 * строения под ними должны читаться (см. FIELD_CLOUDS.overField).
 *
 * ВЬЮГА ГОНИТ ОБЛАКА. Пока над зимним полем идет снежный заряд (часть эффекта
 * подложки), облака летят быстрее — посередине между своей обычной скоростью
 * и скоростью его снега, — и их становится больше (FIELD_CLOUDS.storm). Разгон
 * идет плавно, по силе заряда. Лишние облака приходят из-за наветренного края
 * потоком — по одному, через равные промежутки, все время, пока заряд налетает
 * и держится, — а когда он стихает, тают вместе с ним. Без заряда облака
 * плывут ровно так, как плыли до него.
 *
 * ОРЕЛ НАД ГОРОЙ. Изредка над случайным жетоном горы проступает орел, делает
 * несколько кругов и уходит. Разом кружит не больше двух, над разными горами
 * и разными путями (см. FIELD_EAGLES и utils/effects/buildEagleFlight).
 *
 * ЭФФЕКТ ПОДЛОЖКИ. У каждой подложки — своя небольшая анимация, родная ее
 * пейзажу (порыв ветра над лугом, поземка, блики соли…). Ее ведет отдельный
 * контроллер (scenes/FieldBiomeEffects): отсюда он получает поле, ветер матча
 * и шаг времени. Его сбой остается в нем самом — облака и птицы живут дальше.
 *
 * Чего и где не будет — решает конфиг подложки (configs/fieldAmbience):
 * солнечным картам облака не положены, над пеклом не летают птицы — ни стаи,
 * ни орлы.
 *
 * Все, что здесь случайно, — визуальное: в состояние партии не попадает и у
 * каждого клиента свое.
 */
export default class FieldAmbience {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {Function} [options.rng] — источник случайности (в тестах — заглушка)
   * @param {Object|null} [options.effects] — контроллер эффекта подложки; не
   *   задан — свой, null — без эффекта (в тестах)
   * @param {Object|null} [options.sounds] — голоса птиц (FieldAmbienceSounds);
   *   не заданы — свои, null — птицы молчат
   * @param {Object|null} [options.effectSounds] — звуки эффекта подложки
   *   (FieldEffectSounds): вой вьюги, ветер смерча, порывы ветра, пар
   *   гейзеров; не заданы — свои, null — эффекты молчат и своего звука не ждут
   */
  constructor(scene, {rng = Math.random, effects, sounds, effectSounds} = {}) {
    this.scene = scene;
    this.rng = rng;
    // Звуки эффекта подложки: «буря» (вой вьюги, ветер смерча) не начинается,
    // пока ее звук не загружен, и звучит с картинкой в лад; «порыв» (ветер,
    // пар) звучит, когда его картинка в кадре игрока
    this.effectSounds = effectSounds === undefined ? new FieldEffectSounds(scene) : effectSounds;
    // Голоса птиц: жребий у них свой (не общий rng антуража), чтобы звук не
    // менял то, как летят птицы
    this.sounds = sounds === undefined ? new FieldAmbienceSounds(scene) : sounds;
    this.effects = effects === undefined
      ? new FieldBiomeEffects(scene, {
        rng,
        linearFilter: Phaser.Textures?.FilterMode?.LINEAR,
        canSpawn: (track) => this.checkIfEffectTrackAllowed(track),
      })
      : effects;
    this.field = null;
    this.matchSeed = null;
    this.drift = null;
    // Облака: {model, sprite, shadowSprite}; модели — тем же порядком отдельным
    // списком, чтобы не собирать его заново на каждом кадре
    this.clouds = [];
    this.cloudModels = [];
    // Облака вьюги — те, что прибывают сверх обычных, пока идет снежный заряд:
    // устроены так же. Список их моделей ведет advanceStormClouds
    this.stormClouds = [];
    this.stormCloudModels = [];
    // Ход потока облаков вьюги: сколько ветру осталось пронести облака до
    // прихода следующего (null — поток не начат). Тоже ведет advanceStormClouds
    this.stormCloudStream = {distanceToEntry: null};
    // Заряд вьюги, каким его застал последний шаг (resolveSquall): его сила —
    // вою, сила и скорость потока снега — облакам
    this.squall = NO_SQUALL;
    // Вьюга над облаками: level — насколько она ими владеет (0 — ее нет, 1 — в
    // разгаре), streamSpeed — скорость потока снега последнего заряда (помнится,
    // пока облака не сбавят ход, даже если сам заряд убрали разом), isWaning —
    // заряд стихает: новых облаков он уже не ведет (считается заново на каждом
    // шаге, см. stepCloudStorm)
    this.cloudStorm = {level: 0, streamSpeed: 0, isWaning: false};
    this.flock = null;
    this.nextFlockInMs = 0;
    // Горы поля и орлы над ними: {flight, elapsedMs, mountainKey, sprite}
    this.mountains = [];
    this.eagles = [];
    // null — отсчет до орла еще не начат (гор пока нет)
    this.nextEagleInMs = null;
  }

  /**
   * Пейзаж лег под поле (или поле изменилось: его перекрасили в редакторе) —
   * антураж строится заново под его границы.
   *
   * @param {Object} params
   * @param {{left: number, right: number, top: number, bottom: number}} params.rect —
   *   прямоугольник картинки пейзажа
   * @param {Array<{x: number, y: number}>} params.cellCenters — центры ячеек
   *   игрового поля (без моря)
   * @param {Array<{x: number, y: number}>} [params.mountainCenters] — центры
   *   ячеек с горами: над ними кружат орлы
   * @param {number} params.hexSize
   * @param {string} params.groundBackgroundId
   * @param {string|number|null} [params.matchSeed] — признак партии; null —
   *   партия еще не началась
   * @returns {boolean} построен ли антураж
   */
  setField({rect, cellCenters, mountainCenters, hexSize, groundBackgroundId, matchSeed} = {}) {
    this.clear();
    this.field = null;
    this.mountains = [...(mountainCenters || [])];
    this.nextEagleInMs = null;

    const isRectValid = rect && rect.right > rect.left && rect.bottom > rect.top;
    if (!isRectValid || !(hexSize > 0)) {
      return false;
    }

    const settings = getFieldAmbience(groundBackgroundId);
    // Нет картинки — нет и этой части антуража: лист облаков мог не
    // загрузиться, текстура птицы — не завестись
    const hasClouds = settings.clouds.isEnabled && Boolean(this.scene.textures?.exists?.(FIELD_CLOUDS_SHEET.key));
    const linearFilter = Phaser.Textures?.FilterMode?.LINEAR;
    const hasBirds = settings.birds.isEnabled && ensureBirdTexture(this.scene, {linearFilter});
    // Где нет птиц, нет и орлов
    const hasEagles = settings.birds.isEnabled && ensureEagleTexture(this.scene, {linearFilter});
    const hasOwnAmbience = hasClouds || hasBirds || hasEagles;
    if (!hasOwnAmbience && !this.effects) {
      return false;
    }

    this.field = {
      rect,
      hexSize,
      settings,
      hasClouds,
      hasBirds,
      hasEagles,
      // Карта покрытия поля нужна и облакам, и эффекту подложки
      coverage: hasClouds || this.effects ? buildFieldCoverage({cellCenters, hexSize, rect}) : null,
      birdLane: resolveBirdLane(cellCenters, hexSize),
    };

    if (matchSeed !== null && matchSeed !== undefined) {
      this.matchSeed = String(matchSeed);
    }
    this.drift = resolveDrift(this.matchSeed ?? `до начала партии:${groundBackgroundId}`, settings);

    this.createClouds();
    this.nextFlockInMs = resolveBirdFlockDelayMs(this.rng, true);
    // Эффект подложки — после облаков: он сам решает, есть ли ему что показать
    // на этой подложке, и сам гасит свои сбои
    // Звукам подложку называют до эффекта: первая же его группа уже спросит,
    // можно ли ей родиться
    this.effectSounds?.setGroundBackground(groundBackgroundId);
    const hasEffect = Boolean(this.effects?.setField({
      rect, hexSize, cellCenters, coverage: this.field.coverage, drift: this.drift, groundBackgroundId,
      mountains: this.mountains,
    }));
    if (!hasOwnAmbience && !hasEffect) {
      this.field = null;
      return false;
    }
    return true;
  }

  /**
   * Признак партии из стора — на каждом его обновлении. Партия началась (или в
   * сцену загрузили другую) — ветер пересчитывается; пропал признак (запись
   * матча перематывают через начало) — ветер остается прежним.
   *
   * @param {string|number|null} matchSeed
   */
  setMatchSeed(matchSeed) {
    if (matchSeed === null || matchSeed === undefined || String(matchSeed) === this.matchSeed) {
      return;
    }
    this.matchSeed = String(matchSeed);
    if (!this.field) {
      return;
    }

    const drift = resolveDrift(this.matchSeed, this.field.settings);
    const isSameDrift = this.drift && drift.direction === this.drift.direction && drift.tiltRad === this.drift.tiltRad;
    this.drift = drift;
    // Эффект подложки идет тем же ветром, что и облака
    this.effects?.setDrift(drift);
    if (!isSameDrift) {
      // Облака, плывшие по прежнему ветру, не разворачиваются на месте —
      // раскладываются заново
      this.destroyClouds();
      this.createClouds();
    }
  }

  /**
   * Горы поля изменились (редактор поля поставил или убрал жетон горы) —
   * остальной антураж это не трогает. Орел, чью гору убрали, исчезает с ней.
   *
   * @param {Array<{x: number, y: number}>} mountainCenters — центры ячеек с горами
   */
  setMountains(mountainCenters) {
    this.mountains = [...(mountainCenters || [])];
    // У гор эффект подложки завихряется (вьюга): ему тоже нужны нынешние горы
    this.effects?.setMountains?.(this.mountains);
    if (this.eagles.length === 0) {
      return;
    }
    const mountainKeys = new Set(this.mountains.map(resolvePlaceKey));
    const homeless = this.eagles.filter(({mountainKey}) => !mountainKeys.has(mountainKey));
    if (homeless.length > 0) {
      this.eagles = this.eagles.filter(({mountainKey}) => mountainKeys.has(mountainKey));
      homeless.forEach(({sprite}) => destroySprite(sprite));
    }
  }

  /**
   * Шаг антуража: облака плывут, стая летит или ждет своего часа, орлы кружат.
   *
   * @param {number} deltaMs — время кадра
   */
  update(deltaMs) {
    if (!this.field || !(deltaMs > 0)) {
      return;
    }
    const stepMs = Math.min(deltaMs, FIELD_AMBIENCE_MAX_STEP_MS);
    this.updateClouds(stepMs);
    this.updateBirds(stepMs);
    this.updateEagles(stepMs);
    this.sounds?.update(stepMs);
    this.effects?.update(stepMs);
    // Заряд вьюги читается раз за шаг, после шага картинки. Облака узнают о
    // нем на следующем шаге (см. stepCloudStorm)
    this.squall = this.resolveSquall();
    // Звуки шагают после картинки: группа, родившаяся на этом шаге, на нем же
    // и слышна
    this.effectSounds?.update(stepMs, this.effects);
  }

  /**
   * Можно ли дорожке эффекта подложки рождать группы: решают звуки эффектов —
   * «буря» (вьюга, смерч) не начинается, пока не загружен ее звук: картинка и
   * звук идут только вместе
   */
  checkIfEffectTrackAllowed(track) {
    return !this.effectSounds || this.effectSounds.checkIfTrackAllowed(track);
  }

  /**
   * Идущий заряд вьюги: его сила (0 — заряда нет, 1 — в разгаре) и скорость
   * потока его снега, единиц поля в секунду (0 — неизвестна). Читается раз за
   * шаг: по силе и скорости несутся облака (вой вьюги ведут звуки эффектов —
   * они считают силу заряда сами). Зарядов несколько (прежний доживает после
   * смены ветра) — берется сильнейший.
   *
   * Где эффектам велено молчать, зарядов не ищут вовсе: вьюга идет только
   * вместе со своим воем, и без него облаками она не командует.
   *
   * @returns {{strength: number, streamSpeed: number}}
   */
  resolveSquall() {
    if (!this.effectSounds) {
      return NO_SQUALL;
    }
    const squalls = this.effects?.findLiveGroups?.(SNOW_SQUALL_TAG) || [];
    if (squalls.length === 0) {
      return NO_SQUALL;
    }
    let strength = 0;
    let streamSpeed = 0;
    for (let index = 0; index < squalls.length; index += 1) {
      const {group, elapsedMs} = squalls[index];
      const squallStrength = resolveSnowSquallStrength(group, elapsedMs);
      if (squallStrength > strength) {
        streamSpeed = resolveSnowSquallStreamSpeed(group);
      }
      strength = Math.max(strength, squallStrength);
    }
    // Заряд, не назвавший скорость своего снега, облаками не командует
    return {strength, streamSpeed: Number.isFinite(streamSpeed) && streamSpeed > 0 ? streamSpeed : 0};
  }

  createClouds() {
    const {rect, hexSize, hasClouds} = this.field;
    if (!hasClouds) {
      return;
    }

    buildCloudLayout({rect, hexSize, rng: this.rng, variantCount: FIELD_CLOUDS_SHEET.frameCount}).forEach((model) => {
      this.cloudModels.push(model);
      this.addCloud(this.clouds, model);
    });
    this.placeClouds();
  }

  /**
   * Завести облаку спрайты — обычному и облаку вьюги одинаково: краска, тень
   * и картинка у них по одним правилам.
   *
   * @param {Object[]} clouds — список, в котором облако живет (обычные или вьюги)
   * @param {Object} model
   */
  addCloud(clouds, model) {
    const {settings} = this.field;
    // Облако попадает в список сразу, спрайт за спрайтом: сорвись создание
    // на полпути, уборка найдет и уже созданное
    const entry = {model, shadowSprite: null, sprite: null};
    clouds.push(entry);
    if (settings.clouds.hasShadow) {
      entry.shadowSprite = this.createCloudSprite(FIELD_AMBIENCE_DEPTH.CLOUD_SHADOW);
    }
    entry.sprite = this.createCloudSprite(FIELD_AMBIENCE_DEPTH.CLOUD);
    entry.sprite.setTint(settings.clouds.tint);
    this.applyCloudLook(entry);
  }

  createCloudSprite(depth) {
    const sprite = this.scene.add.image(0, 0, FIELD_CLOUDS_SHEET.key, 0);
    sprite.setOrigin(0.5, 0.5);
    sprite.setDepth(depth);
    return sprite;
  }

  /** Картинка, размер и поворот облака — при создании и при замене ушедшего новым */
  applyCloudLook({model, sprite, shadowSprite}) {
    // Кадры теней идут в листе вторым рядом, в том же порядке, что облака
    sprite.setFrame(model.variant);
    shadowSprite?.setFrame(FIELD_CLOUDS_SHEET.frameCount + model.variant);
    [sprite, shadowSprite].forEach((item) => {
      if (!item) {
        return;
      }
      // Размер — после кадра: он считается от размера кадра
      item.setDisplaySize(model.width, resolveCloudHeight(model));
      item.setRotation(model.rotation);
    });
  }

  updateClouds(stepMs) {
    // Где облаков нет (солнечная подложка, лист не загрузился), нет и облаков вьюги
    if (this.clouds.length === 0) {
      return;
    }

    this.stepCloudStorm(stepMs);
    const {rect, hexSize} = this.field;
    const step = {
      rect, hexSize, drift: this.drift, rng: this.rng, variantCount: FIELD_CLOUDS_SHEET.frameCount,
      // Без вьюги облаками никто не владеет, а соседей нет: шаг тот же, что до нее
      storm: this.cloudStorm,
      neighbours: this.stormCloudModels,
    };
    const respawned = advanceClouds(this.cloudModels, stepMs, step);
    this.refreshCloudLooks(this.clouds, respawned);
    this.updateStormClouds(stepMs, step);
    this.placeClouds();
  }

  /**
   * Вьюга над облаками на этом шаге. Насколько она ими владеет, идет за силой
   * заряда, какой ее застал прошлый шаг (заряд читается после шага картинки,
   * см. update), — плавно, даже если сам заряд убрали разом.
   *
   * @param {number} stepMs
   */
  stepCloudStorm(stepMs) {
    const storm = this.cloudStorm;
    const {strength, streamSpeed} = this.squall;
    if (streamSpeed > 0) {
      storm.streamSpeed = streamSpeed;
    }
    const levelBefore = storm.level;
    // Заряд без скорости потока облаками не командует: как будто его нет
    storm.level = advanceCloudStormLevel(levelBefore, streamSpeed > 0 ? strength : 0, stepMs);
    // Вьюга отпускает облака — заряд стихает (или его убрали разом)
    storm.isWaning = storm.level < levelBefore;
    if (!(storm.level > 0)) {
      storm.streamSpeed = 0;
    }
  }

  /**
   * Облака вьюги: приходят из-за наветренного края потоком, по одному через
   * равные промежутки, пока заряд налетает и держится; ушедшие за край
   * убираются, на смену им в свой черед приходят новые. Заряд стихает — новые
   * не приходят, а оставшиеся тают вместе с ним (см. placeClouds) и
   * убираются, когда он стих.
   *
   * @param {number} stepMs
   * @param {Object} step — ветер и вьюга этого шага, как у обычных облаков
   */
  updateStormClouds(stepMs, step) {
    if (!(this.cloudStorm.level > 0)) {
      // Следующий заряд начнет свой поток заново
      if (this.stormClouds.length > 0 || this.stormCloudStream.distanceToEntry !== null) {
        this.destroyStormClouds();
      }
      return;
    }

    const {arrived, left} = advanceStormClouds(this.stormCloudModels, stepMs, {
      ...step,
      regularClouds: this.cloudModels,
      stream: this.stormCloudStream,
      count: resolveStormCloudCount(this.clouds.length),
    });
    if (left.length > 0) {
      const gone = this.stormClouds.filter((entry) => left.includes(entry.model));
      this.stormClouds = this.stormClouds.filter((entry) => !left.includes(entry.model));
      gone.forEach(({sprite, shadowSprite}) => {
        destroySprite(sprite);
        destroySprite(shadowSprite);
      });
    }
    arrived.forEach((model) => this.addCloud(this.stormClouds, model));
  }

  /** Облакам, которые ушли за край и вернулись новыми, — новая картинка */
  refreshCloudLooks(clouds, respawned) {
    if (respawned.length === 0) {
      return;
    }
    clouds.forEach((entry) => {
      if (respawned.includes(entry.model)) {
        this.applyCloudLook(entry);
      }
    });
  }

  /** Облака и их тени — по местам, с плотностью по месту */
  placeClouds() {
    for (let index = 0; index < this.clouds.length; index += 1) {
      this.placeCloud(this.clouds[index], 1);
    }
    // Облака вьюги проступают и тают вместе с зарядом — как его снег
    const stormAlpha = this.cloudStorm.level;
    for (let index = 0; index < this.stormClouds.length; index += 1) {
      this.placeCloud(this.stormClouds[index], stormAlpha);
    }
  }

  /**
   * @param {{model: Object, sprite: Object, shadowSprite: Object|null}} cloud
   * @param {number} alphaFactor — множитель плотности облака и его тени: 1 — у
   *   обычного облака, сила вьюги — у облака вьюги
   */
  placeCloud({model, sprite, shadowSprite}, alphaFactor) {
    const {rect, coverage, settings} = this.field;
    // Над полем облако прозрачнее; насколько оно над полем — по всему пятну.
    // Дымка темных подложек над полем остается как есть: она и так бледная
    const fieldFactor = settings.clouds.isThinnerOverField
      ? resolveCloudFieldFactor(resolveCloudFieldCoverage(coverage, model))
      : 1;
    const edgeFade = resolveGroundEdgeFade(rect, model.x, model.y);
    placeSprite(
      sprite, model.x, model.y, model.alpha * settings.clouds.alphaFactor * edgeFade * fieldFactor * alphaFactor
    );

    if (shadowSprite) {
      const place = resolveCloudShadowPlace(model);
      // Тень привязана к своему облаку: без облака ее не бывает, и над полем
      // она светлеет вместе с ним
      const shadowEdgeFade = Math.min(edgeFade, resolveGroundEdgeFade(rect, place.x, place.y));
      placeSprite(
        shadowSprite, place.x, place.y,
        FIELD_CLOUDS.shadow.alpha * settings.clouds.shadowAlphaFactor * shadowEdgeFade * fieldFactor * alphaFactor
      );
    }
  }

  updateBirds(stepMs) {
    if (!this.field.hasBirds) {
      return;
    }

    if (!this.flock) {
      this.nextFlockInMs -= stepMs;
      if (this.nextFlockInMs <= 0) {
        this.launchFlock();
      }
      return;
    }

    this.flock.elapsedMs += stepMs;
    if (checkIfBirdFlockDone(this.flock.model, this.flock.elapsedMs)) {
      this.destroyFlock();
      this.nextFlockInMs = resolveBirdFlockDelayMs(this.rng);
      return;
    }
    this.placeBirds();
  }

  launchFlock() {
    const {rect, hexSize, settings, birdLane} = this.field;
    const model = buildBirdFlock({
      area: rect, hexSize, rng: this.rng, lane: birdLane, colors: settings.birds.colors,
    });
    if (!model) {
      this.nextFlockInMs = resolveBirdFlockDelayMs(this.rng);
      return;
    }

    // Стая попадает в поле сразу, птица за птицей: сорвись создание на
    // полпути, уборка найдет и уже созданных
    this.flock = {model, elapsedMs: 0, sprites: []};
    // Пролет иногда озвучен криком этих птиц: у черных и белых стай голос
    // свой, коричневые молчат (см. configs/fieldAmbienceSounds). Крик слышен,
    // только пока стая в кадре игрока
    this.sounds?.schedule({
      soundIds: getFlockSoundIds(model.color),
      // Сколько стая пробудет в кадре: птицы — там и с той плотностью, как
      // их нарисует placeBirds (у края пейзажа они тают)
      resolveInViewMs: () => estimateInViewMs({
        scene: this.scene,
        hexSize,
        durationMs: model.durationMs,
        resolvePoints: (elapsedMs) => model.birds.map((bird) => {
          const pose = resolveBirdPose(model, bird, elapsedMs);
          return {x: pose.x, y: pose.y, alpha: FIELD_BIRDS.alpha * resolveGroundEdgeFade(rect, pose.x, pose.y)};
        }),
      }),
      checkIfAlive: () => this.flock?.model === model,
      checkIfInView: () => checkIfAnySpriteInView(this.scene, this.flock?.sprites, hexSize),
    });
    model.birds.forEach(() => {
      const sprite = this.scene.add.image(0, 0, FIELD_BIRD_TEXTURE_KEY);
      this.flock.sprites.push(sprite);
      sprite.setOrigin(0.5, 0.5);
      sprite.setDepth(FIELD_AMBIENCE_DEPTH.BIRD);
      // Вся стая — одного цвета
      sprite.setTint(model.color);
    });
    this.placeBirds();
  }

  placeBirds() {
    const {rect, hexSize} = this.field;
    const {model, elapsedMs, sprites} = this.flock;
    const baseScale = (hexSize * FIELD_BIRDS.wingspanFactor) / BIRD_TEXTURE_WINGSPAN;

    for (let index = 0; index < sprites.length; index += 1) {
      const bird = model.birds[index];
      const sprite = sprites[index];
      const pose = resolveBirdPose(model, bird, elapsedMs);
      const scale = baseScale * bird.sizeFactor;
      sprite.setRotation(pose.rotation);
      // Птица на текстуре смотрит вправо, крылья — по оси Y: взмах сжимает размах
      sprite.setScale(scale, scale * pose.wingSpread);
      placeSprite(sprite, pose.x, pose.y, FIELD_BIRDS.alpha * resolveGroundEdgeFade(rect, pose.x, pose.y));
    }
  }

  destroyFlock() {
    const sprites = this.flock?.sprites || [];
    this.flock = null;
    sprites.forEach(destroySprite);
  }

  updateEagles(stepMs) {
    if (!this.field.hasEagles) {
      return;
    }

    if (this.eagles.length > 0) {
      const gone = [];
      this.eagles.forEach((eagle) => {
        eagle.elapsedMs += stepMs;
        if (checkIfEagleFlightDone(eagle.flight, eagle.elapsedMs)) {
          gone.push(eagle);
        } else {
          this.placeEagle(eagle);
        }
      });
      if (gone.length > 0) {
        this.eagles = this.eagles.filter((eagle) => !gone.includes(eagle));
        gone.forEach(({sprite}) => destroySprite(sprite));
      }
    }

    // Гор нет — орлам кружить негде, отсчет не идет
    if (this.mountains.length === 0) {
      return;
    }
    if (this.nextEagleInMs === null) {
      this.nextEagleInMs = resolveEagleDelayMs(this.rng, true);
    }
    this.nextEagleInMs -= stepMs;
    if (this.nextEagleInMs <= 0) {
      this.launchEagle();
      // Пауза — от появления орла, а не от его ухода: изредка кружат сразу два
      this.nextEagleInMs = resolveEagleDelayMs(this.rng);
    }
  }

  launchEagle() {
    if (this.eagles.length >= FIELD_EAGLES.maxAtOnce) {
      return;
    }
    // Над одной горой — один орел
    const busyKeys = new Set(this.eagles.map(({mountainKey}) => mountainKey));
    const freeMountains = this.mountains.filter((mountain) => !busyKeys.has(resolvePlaceKey(mountain)));
    if (freeMountains.length === 0) {
      return;
    }
    const mountain = freeMountains[Math.min(freeMountains.length - 1, Math.floor(this.rng() * freeMountains.length))];
    const flight = buildEagleFlight({
      center: mountain,
      hexSize: this.field.hexSize,
      rng: this.rng,
      // Второй орел кружит другим путем: два одинаковых полета разом
      // смотрелись бы копией
      excludeKinds: this.eagles.map((eagle) => eagle.flight.kind),
    });
    if (!flight) {
      return;
    }

    // Орел попадает в список сразу: сорвись создание спрайта, уборка его найдет
    const eagle = {flight, elapsedMs: 0, mountainKey: resolvePlaceKey(mountain), sprite: null};
    this.eagles.push(eagle);
    // И у орла над горой иногда слышен клекот — тоже пока он в кадре
    const {hexSize} = this.field;
    this.sounds?.schedule({
      soundIds: EAGLE_SOUND_IDS,
      // Сколько орел пробудет в кадре: там и с той плотностью, как его
      // нарисует placeEagle (он проступает в начале полета и тает в конце)
      resolveInViewMs: () => estimateInViewMs({
        scene: this.scene,
        hexSize,
        durationMs: flight.durationMs,
        resolvePoints: (elapsedMs) => {
          const pose = resolveEaglePose(flight, elapsedMs);
          return [{x: pose.x, y: pose.y, alpha: FIELD_EAGLES.alpha * pose.visibility}];
        },
      }),
      checkIfAlive: () => this.eagles.includes(eagle),
      checkIfInView: () => checkIfAnySpriteInView(this.scene, [eagle.sprite], hexSize),
    });
    // Орел — картинка владельца игры; не загрузилась — рисованный кодом
    const hasImage = Boolean(this.scene.textures?.exists?.(FIELD_EAGLE_IMAGE.key));
    eagle.textureWingspan = hasImage ? FIELD_EAGLE_IMAGE.wingspan : EAGLE_TEXTURE_WINGSPAN;
    eagle.sprite = this.scene.add.image(0, 0, hasImage ? FIELD_EAGLE_IMAGE.key : FIELD_EAGLE_TEXTURE_KEY);
    eagle.sprite.setOrigin(0.5, 0.5);
    eagle.sprite.setDepth(FIELD_AMBIENCE_DEPTH.EAGLE);
    this.placeEagle(eagle);
  }

  placeEagle({flight, elapsedMs, sprite, textureWingspan}) {
    const pose = resolveEaglePose(flight, elapsedMs);
    const scale = (this.field.hexSize * FIELD_EAGLES.wingspanFactor * flight.sizeFactor) / textureWingspan;
    sprite.setRotation(pose.rotation);
    // Орел на картинке смотрит вправо, крылья — по оси Y: взмах сжимает размах
    sprite.setScale(scale, scale * pose.wingSpread);
    // Орел проступает в начале полета и тает в конце
    placeSprite(sprite, pose.x, pose.y, FIELD_EAGLES.alpha * pose.visibility);
  }

  destroyEagles() {
    const eagles = this.eagles;
    this.eagles = [];
    eagles.forEach(({sprite}) => destroySprite(sprite));
  }

  destroyClouds() {
    const clouds = this.clouds;
    this.clouds = [];
    this.cloudModels = [];
    clouds.forEach(({sprite, shadowSprite}) => {
      destroySprite(sprite);
      destroySprite(shadowSprite);
    });
    // Без обычных облаков нет и облаков вьюги: идет заряд — набегут заново
    this.destroyStormClouds();
  }

  destroyStormClouds() {
    const clouds = this.stormClouds;
    this.stormClouds = [];
    this.stormCloudModels = [];
    // И поток их начнется заново
    this.stormCloudStream.distanceToEntry = null;
    clouds.forEach(({sprite, shadowSprite}) => {
      destroySprite(sprite);
      destroySprite(shadowSprite);
    });
  }

  /** Убрать все спрайты антуража (сцена их больше не покажет) */
  clear() {
    this.destroyClouds();
    this.destroyFlock();
    this.destroyEagles();
    this.sounds?.clear();
    this.effects?.clear();
    // Картинки эффекта больше нет, и шагов времени может не быть: его звуки
    // (вой вьюги, ветер, пар) обрываются
    this.effectSounds?.clear();
    // И облака, которые лягут заново, вьюгу не помнят: плывут обычным ходом
    this.squall = NO_SQUALL;
    this.cloudStorm.level = 0;
    this.cloudStorm.streamSpeed = 0;
  }

  /** Сцена уходит: спрайты убраны, новые не появятся */
  destroy() {
    this.clear();
    this.sounds?.destroy();
    this.effects?.destroy();
    this.effectSounds?.destroy();
    this.field = null;
  }
}
